import { forwardRef, useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Icon } from "../Icon";
import "./styles/actions.scss";

// Links and buttons that are not form submits and not the brand CTA: "view all" links, retry buttons,
// quick actions, icon buttons in table rows. One look for the whole app (styles/actions.scss).

const classes = (base, variant, className) =>
  [base, `${base}_${variant}`, className].filter(Boolean).join(" ");

// Link. `to` -> router Link, otherwise a plain anchor with `href`.
// variant: "more" (e.g. "View all", brand colour), "inline" (underlined, bold), "strong" (bold, for table cells),
// "menu" (full-width entry of a small list). `icon` is placed after the text.
export const ActionLink = ({ to, href, variant = "more", icon, className, children, ...rest }) => {
  const content = (
    <>
      {children}
      {icon && <Icon icon={icon} aria-hidden="true" />}
    </>
  );
  const cls = classes("action_link", variant, className);
  return to !== undefined ? (
    <Link to={to} className={cls} {...rest}>
      {content}
    </Link>
  ) : (
    <a href={href} className={cls} {...rest}>
      {content}
    </a>
  );
};

// Button. With `to` it is a router Link that looks the same.
// variant: "outline" (default, e.g. "Retry"), "primary" (solid brand, the main action of a page), "block" (full width, quick actions), "icon" (square, icon only:
// `label` is its accessible name). `icon` is placed before the text.
export const ActionButton = forwardRef(function ActionButton(
  { to, variant = "outline", icon, label, type = "button", className, children, ...rest },
  ref,
) {
  const cls = classes("action_button", variant, className);
  const content = (
    <>
      {icon && <Icon icon={icon} aria-hidden="true" />}
      {variant === "icon" ? null : children}
    </>
  );
  return to !== undefined ? (
    <Link ref={ref} to={to} className={cls} aria-label={variant === "icon" ? label : undefined} {...rest}>
      {content}
    </Link>
  ) : (
    <button ref={ref} type={type} className={cls} aria-label={variant === "icon" ? label : undefined} {...rest}>
      {content}
    </button>
  );
});

// Button opening a short list of actions (e.g. "Program operation" -> the operation types, "Print" -> what to print).
// items: [{ key, label, to } | { key, label, onClick, disabled, hint }]. Choosing an entry, Escape or a click outside
// closes it. A disabled button entry stays in the list with its hint (why it is disabled) under the label.
// variant: as ActionButton.
export const ActionMenu = ({ label, icon, variant = "outline", items = [], className }) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  const listId = useId();

  useEffect(() => {
    if (!isOpen) return undefined;
    const closeOnOutside = (event) => {
      if (!wrapperRef.current?.contains(event.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", closeOnOutside);
    document.addEventListener("touchstart", closeOnOutside);
    return () => {
      document.removeEventListener("mousedown", closeOnOutside);
      document.removeEventListener("touchstart", closeOnOutside);
    };
  }, [isOpen]);

  const onKeyDown = (event) => {
    if (event.key === "Escape" && isOpen) {
      setIsOpen(false);
      triggerRef.current?.focus();
    }
  };

  return (
    <div className={["action_menu", className].filter(Boolean).join(" ")} ref={wrapperRef} onKeyDown={onKeyDown}>
      <ActionButton
        ref={triggerRef}
        variant={variant}
        icon={icon}
        aria-expanded={isOpen}
        aria-controls={isOpen ? listId : undefined}
        onClick={() => setIsOpen((open) => !open)}
      >
        {label}
        <Icon icon="fa-angle-down" size="sm" aria-hidden="true" />
      </ActionButton>
      {isOpen && (
        <ul id={listId} className="action_menu_list" aria-label={label}>
          {items.map((item) => (
            <li key={item.key}>
              {item.to !== undefined ? (
                <ActionLink to={item.to} variant="menu" onClick={() => setIsOpen(false)}>
                  {item.label}
                </ActionLink>
              ) : (
                <button
                  type="button"
                  className="action_menu_item"
                  disabled={item.disabled}
                  onClick={() => {
                    setIsOpen(false);
                    item.onClick?.();
                  }}
                >
                  {item.label}
                  {item.hint && <span className="action_menu_hint">{item.hint}</span>}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
